import { ColumnDef } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Filter,
  FilterX,
} from "lucide-react";
import { Button } from "@babackapp/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";

export type ProductVariantRow = {
  id: string;
  catalogId: string;
  productId: string;
  variantId: string;
  title: string;
  sku?: string;
  ordersCount: number;
  returnsCount: number;
  returnRate: number;
};

export type ProductRow = {
  id: string;
  catalogId: string;
  title: string;
  image?: string;
  sku?: string;
  ordersCount: number;
  returnsCount: number;
  returnRate: number;
  variants?: ProductVariantRow[];
};

import { getReturnRateColor, getReturnRateColorWithOpacity } from "../../utils/return-rate-colors";

function SortIcon({ isSorted }: { isSorted: false | "asc" | "desc" }) {
  if (isSorted === "asc") {
    return <ArrowUp className="ml-2 h-4 w-4" />;
  }
  if (isSorted === "desc") {
    return <ArrowDown className="ml-2 h-4 w-4" />;
  }
  return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
}

export function getProductsTableColumns(
  t: (key: string) => string,
  options?: {
    showExpandColumn?: boolean;
    onFilterProduct?: (filter: { productId?: string; catalogId?: string; title: string }) => void;
    onClearProductFilter?: () => void;
    activeProductFilter?: { productId?: string; catalogId?: string } | null;
  },
): ColumnDef<ProductRow>[] {
  const columns: ColumnDef<ProductRow>[] = [];

  if (options?.showExpandColumn) {
    columns.push({
      id: "expand",
      header: () => null,
      cell: ({ row }) => {
        const hasVariants = row.original.variants && row.original.variants.length > 0;
        if (!hasVariants) return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => row.toggleExpanded()}
          >
            {row.getIsExpanded() ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        );
      },
      size: 40,
    });
  }

  columns.push(
    {
      accessorKey: "title",
      header: t("analytics.table.product"),
      sortingFn: (rowA, rowB, columnId) => {
        if (rowA.depth > 0) return 0;
        const a = (rowA.getValue(columnId) as string).toLowerCase();
        const b = (rowB.getValue(columnId) as string).toLowerCase();
        return a.localeCompare(b);
      },
      cell: ({ row }) => {
        const image = row.original.image;
        const title = row.getValue("title") as string;
        const isSubRow = row.depth > 0;
        const catalogId = row.original.catalogId;
        const productId = isSubRow
          ? (row.original as unknown as ProductVariantRow).productId
          : row.original.id;

        const handleFilter = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (options?.onFilterProduct) {
            if (isSubRow) {
              options.onFilterProduct({ catalogId, title });
            } else {
              options.onFilterProduct({ productId, title });
            }
          }
        };

        // Check if this row is the currently filtered product
        const activeFilter = options?.activeProductFilter;
        const isFiltered = activeFilter && (
          isSubRow
            ? activeFilter.catalogId === catalogId
            : activeFilter.productId === productId
        );

        const filterButton = options?.onFilterProduct && (
          isFiltered ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); options.onClearProductFilter?.(); }}
                  className="p-1 hover:bg-muted rounded cursor-pointer"
                >
                  <FilterX className="h-3.5 w-3.5 text-primary" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t("analytics.clearProductFilter")}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleFilter}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded cursor-pointer"
                >
                  <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t("analytics.filterByProduct")}</p>
              </TooltipContent>
            </Tooltip>
          )
        );

        // Sub-rows (variants) don't show image, just indented title
        if (isSubRow) {
          return (
            <div className="flex items-center pl-6 gap-2">
              <span className="text-muted-foreground truncate" title={title}>{title}</span>
              {filterButton}
            </div>
          );
        }

        return (
          <div className="flex items-center gap-3 min-w-0">
            {image ? (
              <img
                src={image}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-10 w-10 rounded-md object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                <span className="text-xs text-muted-foreground">N/A</span>
              </div>
            )}
            <span className="font-medium truncate" title={title}>{title}</span>
            {filterButton}
          </div>
        );
      },
    },
    {
      accessorKey: "sku",
      header: t("analytics.table.sku"),
      cell: ({ row }) => {
        const sku = row.getValue("sku") as string | undefined;
        return (
          <span className="text-muted-foreground truncate block" title={sku}>{sku || "-"}</span>
        );
      },
    },
    {
      accessorKey: "ordersCount",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 -ml-2"
          >
            {t("analytics.table.orders")}
            <SortIcon isSorted={column.getIsSorted()} />
          </Button>
        );
      },
      cell: ({ row }) => {
        const count = row.getValue("ordersCount") as number;
        return <div className="text-center">{count}</div>;
      },
    },
    {
      accessorKey: "returnsCount",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 -ml-2"
          >
            {t("analytics.table.returns")}
            <SortIcon isSorted={column.getIsSorted()} />
          </Button>
        );
      },
      cell: ({ row }) => {
        const count = row.getValue("returnsCount") as number;
        return <div className="text-center">{count}</div>;
      },
    },
    {
      accessorKey: "returnRate",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 -ml-2"
          >
            {t("analytics.table.returnRate")}
            <SortIcon isSorted={column.getIsSorted()} />
          </Button>
        );
      },
      cell: ({ row }) => {
        const rate = row.getValue("returnRate") as number;
        const color = getReturnRateColor(rate);
        const trackColor = getReturnRateColorWithOpacity(rate, 0.2);
        const clampedRate = Math.min(rate, 100);
        return (
          <div className="flex items-center gap-2">
            <div
              className="h-2 w-16 rounded-full overflow-hidden flex-shrink-0"
              style={{ backgroundColor: trackColor }}
            >
              <div
                className="h-full rounded-full"
                style={{ width: `${clampedRate}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-sm font-medium">
              {rate.toFixed(1)}%
            </span>
          </div>
        );
      },
    },
  );

  return columns;
}
